import { Box, Typography } from '@mui/material';
import { navigate } from 'gatsby';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ResourceFormWrapper from '@components/ResourceFormWrapper/ResourceFormWrapper';

import {
  OCR_ENABLED_FORM_TYPES,
  RESOURCE_CURATION_CONFIG,
  normalizeFormType,
} from '@constants/RESOURCE_CURATION_CONFIG';

import { CurationModeProvider } from '@contexts/CurationModeContext';

import { useAutoCuration } from '@hooks/useAutoCuration';
import { useLoadForms } from '@hooks/useLoadForms';
import { useSnackbar } from '@hooks/useSnackbar';

import { createEmptyRecordFromFormType } from '@utils/createEmptyRecordFromFormType';
import type { FormType } from '@utils/fhir-mappers';
import { mapBBoxTitleToFormType } from '@utils/mapBBoxTitleToFormType';
import { applyLocationFacility } from '@utils/ocr/locationFacility';
import { mergeOcrFromConfig } from '@utils/ocr/mergeFromConfig';

import { ResourceSourceType } from 'generated/types';

import type { BBoxResource } from '../../../types/bbox';

const messages = defineMessages({
  successMessage: {
    id: 'successMessage.updateDiscernResultStatus',
    defaultMessage: 'Successfully updated selected resource',
  },
  errorMessage: {
    id: 'errorMessage.updateDiscernResultStatus',
    defaultMessage: 'Failed to update selected resource',
  },
});
/* --------------------------------------------------------------------------
   Export types for parent
-------------------------------------------------------------------------- */

export type FormsMeta = {
  totalResources: number;
  activeIndex: number;
  canGoPrev: boolean;
  canGoNext: boolean;
  formsLoaded: boolean;
};

export type DynamicResourceFormsHandle = {
  goNext: () => void;
  goPrevious: () => void;
  addManualForm: (formType: FormType) => void;
};

/* --------------------------------------------------------------------------
   Internal helpers
-------------------------------------------------------------------------- */

export type NormalizedResource = {
  id: string;
  nextId: string | null;
  discern_result_id: string | null;
  formType: FormType;
  record: any;
  isManual: boolean;
  label: string;
};

type BBoxField = BBoxResource['Resource_data'][number];
type BBoxMap = Record<string, BBoxField[]>;

function buildFlatAndMap(resource: BBoxResource): {
  flat: Record<string, unknown>;
  bboxMap: BBoxMap;
} {
  const flat: Record<string, unknown> = {};
  const bboxMap: BBoxMap = {};

  for (const field of resource.Resource_data) {
    if (!field.label) continue;
    if (!(field.label in bboxMap)) bboxMap[field.label] = [];
    bboxMap[field.label].push(field);

    const { text } = field;
    // AutoCuration sends dose_value, duration_value, timing_frequency etc as a
    // JSON number, as_needed as a boolean, and timing_when as an array — only
    // string text was ever copied here, so mergeOcrFromConfig's `record[label]`
    // fallbacks (scalarValue, objectValue) never had anything to read and these
    // fields were silently dropped before reaching the OCR assembler (VEG-804).
    if (typeof text === 'string' && text.trim()) {
      flat[field.label] = text;
    } else if (typeof text === 'number' || typeof text === 'boolean') {
      flat[field.label] = text;
    } else if (Array.isArray(text) && text.length) {
      flat[field.label] = text;
    }
  }

  return { flat, bboxMap };
}

export function buildAutoCurationRecord(
  rawFormType: FormType,
  resource: BBoxResource,
  locationResource?: BBoxResource | null
): any {
  const normalizedType = normalizeFormType(rawFormType as unknown as string);
  if (!(normalizedType in RESOURCE_CURATION_CONFIG)) {
    return {};
  }
  const cfg = RESOURCE_CURATION_CONFIG[normalizedType];

  const { flat, bboxMap } = buildFlatAndMap(resource);

  // The facility lives on the group's own `Location` resource, not on this one.
  // Injected into the bboxMap so it reads like any other OCR label — the config's
  // ocrMap decides which form types consume it.
  applyLocationFacility(bboxMap, locationResource);

  const baseDefaults = cfg.defaults;

  const base = {
    ...JSON.parse(JSON.stringify(baseDefaults)),
    ...flat,
    recordSource: ResourceSourceType.discern,
    _bboxMap: bboxMap,
  };

  if (OCR_ENABLED_FORM_TYPES.includes(normalizedType)) {
    const merged = mergeOcrFromConfig(normalizedType, base) || {};
    return {
      ...base,
      ...merged,
      selfReported:
        typeof base.selfReported === 'boolean' ? base.selfReported : false,
      associatedResources: base.associatedResources ?? [],
    };
  }

  return {
    ...base,
    title:
      flat.title ||
      flat.name ||
      flat.condition ||
      flat.procedureName ||
      flat.visitReason ||
      'Untitled',
  };
}

export function buildAddedAutoCurationRecord(formType: FormType): any {
  return {
    ...createEmptyRecordFromFormType(formType),
    recordSource: ResourceSourceType.pdf,
  };
}

/* --------------------------------------------------------------------------
   Component
-------------------------------------------------------------------------- */

type Props = {
  discern_result_id: string;
  bboxData: BBoxResource[];
  /**
   * The `Location` resource extracted for this group, if the model found one.
   * Shared by every resource in the group — it is where the facility comes from.
   */
  locationResource?: BBoxResource | null;
  totalResources: number;
  patientId?: string;
  nextId: string | null;
  activeIndex: number;
  bbox_id: string;
  pdfUrl?: string | null;
  setActiveIndex: React.Dispatch<React.SetStateAction<number>>;
  onMetaChange?: (meta: FormsMeta) => void;
  onFormsMapChange?: (formTypes: FormType[]) => void;

  /**
   * When true, renders the form with minimal wrapper styling suited for
   * the PDF curation side-by-side view. Does not affect forms rendered elsewhere.
   */
  curationMode?: boolean;

  /**
   * Optional callback fired when a resource form is saved.
   * Used by auto-curation drilldown to mark results complete.
   */
  onResourceSaved?: (
    discern_result_id: string,
    resource: BBoxResource | null,
    nextId?: string | null
  ) => void | Promise<void>;
};

const DynamicResourceForms = React.forwardRef<
  DynamicResourceFormsHandle,
  Props
>(
  (
    {
      discern_result_id,
      bboxData,
      locationResource,
      bbox_id,
      totalResources,
      nextId,
      patientId,
      activeIndex,
      pdfUrl,
      setActiveIndex,
      onMetaChange,
      onFormsMapChange,
      curationMode = false,
      onResourceSaved,
    },
    ref
  ) => {
    const { forms, loading } = useLoadForms(bboxData);

    const { state, addManualResource, removeManualResource } =
      useAutoCuration();
    const { enqueueSnackbar } = useSnackbar();
    const { formatMessage } = useIntl();

    const { manualResources = [] } = state;

    /* ----------------------------------------
       Build auto-curation resources
    ---------------------------------------- */
    const autoResources = React.useMemo<NormalizedResource[]>(() => {
      if (Object.keys(forms).length === 0) return [];

      const index = Number(0);
      if (!Number.isInteger(index) || index < 0 || index >= bboxData.length) {
        return [];
      }

      const res = bboxData[index];
      const rawType = mapBBoxTitleToFormType(res.Resource_title);
      return [
        {
          id: `auto-${bbox_id}`,
          nextId,
          formType: rawType as FormType,
          discern_result_id,
          record: buildAutoCurationRecord(
            rawType as FormType,
            res,
            locationResource
          ),
          isManual: false,
          label: `${rawType} #${index + 1}`,
        },
      ];
    }, [forms, bboxData, locationResource, bbox_id, nextId, discern_result_id]);

    const allResources = React.useMemo(
      () => [...autoResources, ...manualResources],
      [autoResources, manualResources]
    );

    /* ----------------------------------------
       Clamp activeIndex
    ---------------------------------------- */

    /* ----------------------------------------
       Imperative: add manual form
    ---------------------------------------- */
    const addManualForm = React.useCallback(
      (formType: FormType) => {
        const newRecord = buildAddedAutoCurationRecord(formType);

        setActiveIndex((i) => i + 1);

        addManualResource({
          id: `manual-${activeIndex + 1}-${formType}`,
          formType,
          nextId: nextId,
          discern_result_id: null,
          record: newRecord,
          isManual: true,
          label: `Manual ${formType} #${manualResources.length + 1}`,
        });
      },
      [
        setActiveIndex,
        addManualResource,
        activeIndex,
        nextId,
        manualResources.length,
      ]
    );

    /* ----------------------------------------
       Imperative: navigation
    ---------------------------------------- */
    const goNext = React.useCallback(
      () => setActiveIndex((i) => i + 1),
      [setActiveIndex]
    );

    const goPrevious = React.useCallback(
      () => setActiveIndex((i) => Math.max(0, i - 1)),
      [setActiveIndex]
    );

    /* ----------------------------------------
       Expose imperative handle to parent
    ---------------------------------------- */
    React.useImperativeHandle(
      ref,
      () => ({
        goNext,
        goPrevious,
        addManualForm,
      }),
      [goNext, goPrevious, addManualForm]
    );

    /* ----------------------------------------
       Notify parent meta
    ---------------------------------------- */
    const formsLoaded = !loading && Object.keys(forms).length > 0;

    React.useEffect(() => {
      if (!onMetaChange) return;

      onMetaChange({
        totalResources,
        activeIndex,
        canGoPrev: totalResources > 0 && activeIndex > 0,
        canGoNext: totalResources > 0 && activeIndex < totalResources - 1,
        formsLoaded,
      });
    }, [onMetaChange, totalResources, activeIndex, formsLoaded]);

    /* ----------------------------------------
       Send available form types to parent
    ---------------------------------------- */
    React.useEffect(() => {
      if (!onFormsMapChange) return;
      onFormsMapChange(Object.keys(forms) as FormType[]);
    }, [forms, onFormsMapChange]);

    /* ----------------------------------------
       Render
    ---------------------------------------- */
    let body: React.ReactNode;

    if (loading && totalResources === 0) {
      body = (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        </Box>
      );
    } else if (totalResources === 0) {
      body = (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No resources detected.
          </Typography>
        </Box>
      );
    } else if (activeIndex >= totalResources) {
      body = (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          <Typography variant="body1" color="text.secondary">
            All resources reviewed.
          </Typography>
        </Box>
      );
    } else {
      const resource =
        allResources.find((r) => r.id.startsWith('manual-' + activeIndex)) ??
        allResources.find((r) => r.id === `auto-${bbox_id}`);
      const formType = resource?.formType || null;
      const FormComponent = formType
        ? formType in forms
          ? forms[formType]
          : undefined
        : undefined;

      if (!FormComponent) {
        body = (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Loading form…
            </Typography>
          </Box>
        );
      } else if (resource) {
        body = (
          <CurationModeProvider curationMode={curationMode}>
            <ResourceFormWrapper
              key={resource.id}
              bboxId={resource.id}
              pdfUrl={pdfUrl}
              nextId={resource.nextId}
              discern_result_id={resource.discern_result_id}
              record={resource.record}
              formType={resource.formType}
              FormComponent={FormComponent}
              patientId={patientId}
              isAutoCurating={!resource.isManual}
              onSubmit={async () => {
                if (onResourceSaved) {
                  const idx = resource.id.startsWith('auto-')
                    ? parseInt(resource.id.replace('auto-', ''), 10)
                    : -1;
                  const original =
                    idx >= 0 && idx < bboxData.length ? bboxData[idx] : null;
                  if (!resource.isManual) {
                    await onResourceSaved(discern_result_id, original, nextId);
                  }
                }

                if (resource.isManual) {
                  removeManualResource(resource.id);
                  navigate(`${window.location.pathname}?result_id=${nextId}`, {
                    replace: true,
                  });
                  enqueueSnackbar({
                    message: formatMessage(messages.successMessage),
                    variant: 'success',
                  });
                }
              }}
            />
          </CurationModeProvider>
        );
      }
    }

    // In curation mode, render without the extra wrapper for a cleaner side-by-side view
    if (curationMode) {
      return <div className="dynamic-forms w-full h-full">{body}</div>;
    }

    return (
      <div className="dynamic-forms w-full h-full">
        <div className="bg-white rounded-xl shadow-sm p-4 h-full">{body}</div>
      </div>
    );
  }
);

export default DynamicResourceForms;
